import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, PageBody, StatusBadge, EmptyState } from "../components/Shared";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", title: "", notes: "", valid_until: "" });
  const [filterCustomer, setFilterCustomer] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const load = () => Promise.all([
    api.get("/offers").then((r) => setOffers(r.data)),
    api.get("/customers").then((r) => setCustomers(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const statusOptions = [...new Set(offers.map((o) => o.status).filter(Boolean))].sort();
  const filteredOffers = offers.filter((o) =>
    (filterCustomer === "all" || o.customer_id === filterCustomer) &&
    (filterStatus === "all" || o.status === filterStatus)
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) return toast.error("Select a customer");
    try {
      const { data } = await api.post("/offers", { ...form, items: [] });
      toast.success(`Offer ${data.offer_number} created`);
      setOpen(false); setForm({ customer_id: "", title: "", notes: "", valid_until: "" });
      load();
    } catch { toast.error("Failed to create"); }
  };

  const del = async (e, o) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Delete offer ${o.offer_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/offers/${o.id}`);
      toast.success(`Offer ${o.offer_number} deleted`);
      load();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <>
      <PageHeader
        title="Offers"
        subtitle="Sales pipeline"
        testid="offers-title"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-offer-btn"><Plus className="w-4 h-4 mr-2" /> New Offer</Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-lg">
              <DialogHeader><DialogTitle className="font-display tracking-tight">New Offer</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3">
                <div>
                  <Label className="label-caps">Customer</Label>
                  <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                    <SelectTrigger className="rounded-none mt-1" data-testid="offer-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent className="rounded-none">
                      {customers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`offer-cust-opt-${c.id}`}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="label-caps">Title</Label><Input className="rounded-none mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="offer-title-input" /></div>
                <div><Label className="label-caps">Valid until</Label><Input type="date" className="rounded-none mt-1" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="offer-valid-until" /></div>
                <div><Label className="label-caps">Notes</Label><Textarea rows={3} className="rounded-none mt-1" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="offer-notes" /></div>
                <DialogFooter><Button type="submit" className="rounded-none bg-blue-600 hover:bg-blue-700" data-testid="offer-save-btn">Create</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        <div className="flex items-center gap-3 mb-4" data-testid="offers-filters">
          <div className="w-56">
            <Select value={filterCustomer} onValueChange={setFilterCustomer}>
              <SelectTrigger className="rounded-none" data-testid="offers-filter-customer"><SelectValue placeholder="Müşteri" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" data-testid="offers-filter-customer-all">Tüm müşteriler</SelectItem>
                {customers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`offers-filter-customer-${c.id}`}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="rounded-none" data-testid="offers-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all" data-testid="offers-filter-status-all">Tüm statusler</SelectItem>
                {statusOptions.map((s) => <SelectItem key={s} value={s} data-testid={`offers-filter-status-${s}`}>{s.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(filterCustomer !== "all" || filterStatus !== "all") && (
            <Button variant="outline" className="rounded-none border-slate-300" onClick={() => { setFilterCustomer("all"); setFilterStatus("all"); }} data-testid="offers-filter-clear">
              Temizle
            </Button>
          )}
          <span className="text-xs text-slate-500 font-mono ml-auto" data-testid="offers-filter-count">{filteredOffers.length} / {offers.length} teklif</span>
        </div>
        {offers.length === 0 ? <EmptyState title="No offers yet" hint="Create an offer, then upload items via Excel." /> :
          filteredOffers.length === 0 ? <EmptyState title="Sonuç yok" hint="Filtreleri temizleyip tekrar deneyin." /> :
          <div className="bg-white border border-slate-300">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-300"><tr className="text-left">
                <th className="py-2 px-3 label-caps">Offer #</th>
                <th className="py-2 px-3 label-caps">Customer</th>
                <th className="py-2 px-3 label-caps">Title</th>
                <th className="py-2 px-3 label-caps text-right">Items</th>
                <th className="py-2 px-3 label-caps">Status</th>
                <th className="py-2 px-3 label-caps">Created</th>
                <th className="py-2 px-3 w-10"></th>
              </tr></thead>
              <tbody>
                {filteredOffers.map((o) => (
                  <tr key={o.id} className="border-b border-slate-200 hover:bg-slate-50 stagger-row" data-testid={`offer-row-${o.id}`}>
                    <td className="py-2 px-3 font-mono"><Link to={`/offers/${o.id}`} className="text-blue-700 hover:text-blue-900">{o.offer_number}</Link></td>
                    <td className="py-2 px-3">{o.customer_name}</td>
                    <td className="py-2 px-3">{o.title || "-"}</td>
                    <td className="py-2 px-3 font-mono text-right">{o.item_count}</td>
                    <td className="py-2 px-3"><StatusBadge status={o.status} /></td>
                    <td className="py-2 px-3 font-mono text-slate-500">{o.created_at?.slice(0, 10)}</td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={(e) => del(e, o)}
                        className="text-slate-400 hover:text-red-600 transition-colors duration-200"
                        title="Delete offer"
                        data-testid={`del-offer-${o.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
      </PageBody>
    </>
  );
}
